import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  MessageSquare,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";

// Form schema with validation
const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().optional(),
  subject: z
    .string()
    .min(5, { message: "Subject must be at least 5 characters" }),
  message: z
    .string()
    .min(10, { message: "Message must be at least 10 characters" }),
  inquiryType: z.enum(
    [
      "general",
      "rental",
      "property-management",
      "partnership",
      "careers",
      "other",
    ],
    {
      required_error: "Please select an inquiry type",
    }
  ),
  preferredContact: z.enum(["email", "phone"], {
    required_error: "Please select your preferred contact method",
  }),
});

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Initialize form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      inquiryType: "general",
      preferredContact: "email",
    },
  });

  // Form submission handler
  function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      console.log(values);
      setIsSubmitting(false);
      setIsSuccess(true);

      toast("Message Sent", {
        description:
          "We've received your message and will get back to you soon.",
      });

      form.reset();

      // Reset success state after 5 seconds
      setTimeout(() => setIsSuccess(false), 5000);
    }, 1500);
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
        <p className="text-lg text-muted-foreground">
          Have questions or need assistance? We&apos;re here to help. Reach out to
          our team using the form below or through our contact information.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Contact Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-6">
              {isSuccess ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                  <h3 className="text-2xl font-bold mb-2">Message Sent!</h3>
                  <p className="text-muted-foreground mb-6">
                    Thank you for reaching out. Our team will get back to you
                    shortly.
                  </p>
                  <Button onClick={() => setIsSuccess(false)}>
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Your name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="your.email@example.com"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone (Optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="(123) 456-7890" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="inquiryType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Inquiry Type</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select inquiry type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="general">
                                  General Inquiry
                                </SelectItem>
                                <SelectItem value="rental">
                                  Rental Information
                                </SelectItem>
                                <SelectItem value="property-management">
                                  Property Management
                                </SelectItem>
                                <SelectItem value="partnership">
                                  Business Partnership
                                </SelectItem>
                                <SelectItem value="careers">Careers</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subject</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Subject of your message"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="How can we help you?"
                              className="min-h-[120px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="preferredContact"
                      render={({ field }) => (
                        <FormItem className="space-y-3">
                          <FormLabel>Preferred Contact Method</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="flex flex-col space-y-1"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="email" id="email" />
                                <FormLabel
                                  htmlFor="email"
                                  className="font-normal cursor-pointer"
                                >
                                  Email
                                </FormLabel>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="phone" id="phone" />
                                <FormLabel
                                  htmlFor="phone"
                                  className="font-normal cursor-pointer"
                                >
                                  Phone
                                </FormLabel>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="mr-2">Sending...</span>
                          <Send className="h-4 w-4 animate-pulse" />
                        </>
                      ) : (
                        <>
                          <span className="mr-2">Send Message</span>
                          <Send className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Contact Information */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6 space-y-6">
              <h2 className="text-2xl font-bold">Contact Information</h2>

              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 mr-3 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Main Office</p>
                    <p className="text-muted-foreground">123 Property Street</p>
                    <p className="text-muted-foreground">
                      Real Estate City, NY 10001
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Phone className="h-5 w-5 mr-3 text-primary" />
                  <div>
                    <p className="font-medium">Phone</p>
                    <p className="text-muted-foreground">(123) 456-7890</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Mail className="h-5 w-5 mr-3 text-primary" />
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-muted-foreground">info@homespaces.com</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="h-5 w-5 mr-3 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Office Hours</p>
                    <p className="text-muted-foreground">
                      Monday - Friday: 9:00 AM - 6:00 PM
                    </p>
                    <p className="text-muted-foreground">
                      Saturday: 10:00 AM - 4:00 PM
                    </p>
                    <p className="text-muted-foreground">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-4">Regional Offices</h2>

              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="new-york">
                  <AccordionTrigger>New York</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      <p>123 Property Street</p>
                      <p>New York, NY 10001</p>
                      <p>Phone: (212) 555-1234</p>
                      <p>Email: nyc@homespaces.com</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="los-angeles">
                  <AccordionTrigger>Los Angeles</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      <p>456 Real Estate Avenue</p>
                      <p>Los Angeles, CA 90001</p>
                      <p>Phone: (310) 555-5678</p>
                      <p>Email: la@homespaces.com</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="chicago">
                  <AccordionTrigger>Chicago</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      <p>789 Housing Boulevard</p>
                      <p>Chicago, IL 60601</p>
                      <p>Phone: (312) 555-9012</p>
                      <p>Email: chicago@homespaces.com</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold mb-4">Live Chat</h2>
              <div className="bg-muted rounded-lg p-4 text-center">
                <MessageSquare className="h-10 w-10 mx-auto mb-2 text-primary" />
                <p className="mb-4">
                  Need immediate assistance? Chat with our support team.
                </p>
                <Button>Start Chat</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Map Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6">Find Us</h2>
        <div className="bg-muted rounded-lg h-[400px] flex items-center justify-center">
          <div className="text-center">
            <MapPin className="h-10 w-10 mx-auto mb-2 text-primary" />
            <p className="text-muted-foreground mb-2">Interactive Map</p>
            <p className="text-sm text-muted-foreground">
              (In a real app, this would be an interactive map showing office
              locations)
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="faq-1">
              <AccordionTrigger>
                How quickly can I expect a response?
              </AccordionTrigger>
              <AccordionContent>
                We strive to respond to all inquiries within 24 business hours.
                For urgent matters, we recommend using our live chat feature
                during business hours for immediate assistance.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2">
              <AccordionTrigger>
                Can I schedule a property viewing through this form?
              </AccordionTrigger>
              <AccordionContent>
                Yes, you can request a property viewing through this form.
                Simply select &quot;Rental Information&quot; as your inquiry type and
                mention the specific property you&apos;re interested in viewing in
                your message. Alternatively, you can schedule viewings directly
                from our property listings page.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3">
              <AccordionTrigger>
                Do you offer virtual consultations?
              </AccordionTrigger>
              <AccordionContent>
                Yes, we offer virtual consultations via video call for clients
                who cannot visit our offices in person. Please mention in your
                message that you&apos;d prefer a virtual consultation, and we&apos;ll
                arrange it accordingly.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-4">
              <AccordionTrigger>How can I join your team?</AccordionTrigger>
              <AccordionContent>
                We&apos;re always looking for talented individuals to join our team.
                Select &quot;Careers&quot; as your inquiry type and attach your resume to
                your message. You can also visit our Careers page for current
                openings.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-5">
              <AccordionTrigger>
                I&apos;m a property owner. How can I list my property with you?
              </AccordionTrigger>
              <AccordionContent>
                Property owners can select &quot;Property Management&quot; as the inquiry
                type and provide details about your property. Our property
                management team will contact you to discuss our services and how
                we can help you manage or list your property.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
